<template>
  <sl-tooltip :disabled="!ifHasTooltipContentSlot || isTooltipDisabled" hoist :placement="tooltipPlacement" :style="tooltipMaxWidthPx">
    <div slot="content">
      <slot name="tooltipContent"></slot>
    </div>
    <sl-input :class="[disabled ? 'disabled': '', inputCssClass]" :disabled="disabled" :placeholder="placeholder" :value="inputValue" clearable 
      @sl-input="handleValueInput" 
      @sl-change="handleValueChange" 
      @sl-clear="handleValueClear"
      @keyup.enter="handleValueSubmit">
      <span slot="prefix" class="search-icon icon-search-loupe-lens"></span>
      <span slot="clear-icon" class="search-icon icon-close-cross-exit-quit-delete"></span>
    </sl-input>
  </sl-tooltip>
</template>

<script lang="ts" setup>
import { computed, ref, toRefs, useSlots, watch } from 'vue';
import _ from 'lodash';
import "@shoelace-style/shoelace/dist/components/tooltip/tooltip.js";
import "@shoelace-style/shoelace/dist/components/input/input.js";

interface IMcSearchInputProps {
  inputValue?: string;
  inputCssClass?: string;
  disabled?: boolean;
  placeholder?: string;
  minValueLength?: number;
  tooltipMaxWidth?: number;
  tooltipPlacement?: string;
  isTooltipDisabled?: boolean;
}

const props = withDefaults(defineProps<IMcSearchInputProps>(), {
  inputValue: "",
  inputCssClass: "",
  disabled: false,
  placeholder: "Search...",
  minValueLength: 1,
  tooltipMaxWidth: 160,
  tooltipPlacement: "bottom",
  isTooltipDisabled: true,
});

const { 
  inputValue,
  inputCssClass,
  disabled, 
  placeholder, 
  minValueLength, 
  tooltipMaxWidth,
  tooltipPlacement, 
  isTooltipDisabled } = toRefs(props);

const slots = useSlots() as ReturnType<typeof useSlots>;;
const ifHasTooltipContentSlot = computed(() => !!slots.tooltipContent);
const tooltipMaxWidthPx = computed(() => `--max-width: ${tooltipMaxWidth.value}px;`);
const changedValue = ref<string|undefined>(_.cloneDeep(inputValue.value));

const emit = defineEmits([
  'input',
  'change',
  'clear',
  'submit',
]);

const handleValueInput = (event: Event) => {
  if (disabled.value) return;
  setChangedValue(event, false);
  emit('input', changedValue.value);
};

const handleValueChange = (event: Event) => {
  if (disabled.value) return;
  setChangedValue(event);
  emit('change', changedValue.value);
};

const handleValueClear = () => {
  if (disabled.value) return;
  changedValue.value = undefined;
  emit('clear', changedValue.value);
  emit('submit', changedValue.value);
};

const handleValueSubmit = (event: Event) => {
  if (disabled.value) return;
  setChangedValue(event);

  if (changedValue.value && changedValue.value.length && changedValue.value.length < minValueLength.value) {
    return;
  }

  emit('submit', changedValue.value);
};

const setChangedValue = (event: Event, trimValue: boolean = true) => {
  const target = event?.target as HTMLInputElement;
  changedValue.value = trimValue ? target.value?.trim() : target.value;
}

watch(
  () => inputValue.value,
  () => changedValue.value = _.cloneDeep(inputValue.value)
);
</script>

<style>
:root {
  --search-input-text-color: var(--colour-text-neutral-default);
  --search-input-placeholder-color: var(--colour-text-disabled);
  --search-input-loupe-icon-color: var(--colour-icons-neutral-subtle);
  --search-input-cross-icon-color: var(--colour-icons-contrast-dark);
  --search-input-background-color: transparent;
  --search-input-background-color-hover: transparent;
  --search-input-background-color-focus: transparent;
  --search-input-border-color: var(--colour-border-neutral-subtle);
  --search-input-border-color-hover: var(--colour-border-neutral-subtle);
  --search-input-border-color-active: var(--colour-border-brand-default);
  --search-input-border-color-focus: var(--colour-border-brand-default);
  --search-input-ring-width-focus: 2px;
}
</style>

<style lang="scss" scoped>
  sl-input {
    --sl-input-background-color: var(--search-input-background-color, var(--sl-input-background-color));
    --sl-input-background-color-hover: var(--search-input-background-color-hover, var(--sl-input-background-color-hover));
    --sl-input-background-color-focus: var(--search-input-background-color-focus, var(--sl-input-background-color-focus));
    --sl-input-border-color: var(--search-input-border-color, var(--sl-input-border-color));
    --sl-input-border-color-active: var(--search-input-border-color-active, var(--sl-input-border-color-active));
    --sl-input-border-color-hover: var(--search-input-border-color-hover, var(--sl-input-border-color-hover));
    --sl-input-border-color-focus: var(--search-input-border-color-focus, var(--sl-input-border-color-focus));
    --sl-input-focus-ring-color: var(--search-input-border-color-focus, var(--sl-input-focus-ring-color));
    --sl-focus-ring-width: var(--search-input-ring-width-focus, var(--sl-focus-ring-width));

    &::part(input) {
      color: var(--search-input-text-color);
      padding: 0;

      &::placeholder {
        color: var(--search-input-placeholder-color);
      }
    }

    &::part(clear-button){
      width: initial;
    }
  }

  sl-input.disabled {
    &::part(prefix) {
      cursor: not-allowed;
    }
  }

  .search-icon {
    font-size: 25px;

    &.icon-search-loupe-lens {
      margin-left: 2px;
      color: var(--search-input-loupe-icon-color);
    }

    &.icon-close-cross-exit-quit-delete {
      cursor: pointer;
      margin: 0;
      color: var(--search-input-cross-icon-color);
    }
  }
</style>